import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { translate } from 'react-i18next';
import { Field, FieldArray, formValueSelector } from 'redux-form';

import Cross from '../../assets/cross';
import TextInput, { Wrapper as TextInputWrapper } from '../fields/TextInput';
import Heading from '../elements/PageHeading';
import Button from '../elements/Button';
import renderError from '../elements/renderError';

import Slider from '../fields/Slider';
import RadioField from '../fields/RadioField';
import { FormSelect } from '../fields/FormSelect';

import { WIZARD_FORM_NAME } from '../../constants/common';
import { hasOptionalSetting } from '../../utils/deviceTypes';

const shelfIds = [
  { label: 'shelf 1', value: 'shelf1' },
  { label: 'shelf 2', value: 'shelf2' },
  { label: 'shelf 3', value: 'shelf3' },
  { label: 'shelf 4', value: 'shelf4' },
  { label: 'shelf 5', value: 'shelf5' },
  { label: 'shelf 6', value: 'shelf6' },
];

const slotIds = [
  { label: '2 slots', value: 'slot2' },
  { label: '3 slots', value: 'slot3' },
  { label: '4 slots', value: 'slot4' },
  { label: '6 slots', value: 'slot6' },
];

const SliderWrapper = styled.div`
  margin: 20px 40px;
`;

const CardPaymentWrapper = styled.div`
  text-align: center;
  margin: 30px 40px;
`;

const CardPaymentErrorWrapper = styled.div`
  text-align: center;
  margin: 20px 20px;
`;

const AddWrapper = styled.div`
  text-align: center;
  &:after {
    content: '';
    display: block;
    border-bottom: solid 1px ${props => props.theme.primaryAlt};
    margin: 40px 60px auto;
  }
  & ${TextInputWrapper}:first-child {
    margin-top: 0px;
  }
`;

const SubHeading = styled(Heading)`
  margin-top: 20px;
  min-height: 0px;
`;
// skip forbiden characters from tagname -> $, #, ., space
const skipAsciiTagCharacter = [36, 35, 46, 32];

const TagAdder = translate()(
  class extends Component {
    state = {
      name: '',
      value: '',
    };

    add = () => {
      if (this.state.name === '' || !this.state.value === '') {
        return;
      }
      this.props.onAdd(this.state);
      this.setState({ name: '', value: '' });
    };

    inputTagNameValue(name) {
      const currentKeyAscii = name.charCodeAt(name.length - 1);
      if (skipAsciiTagCharacter.includes(currentKeyAscii)) {
        return;
      }

      this.setState({ name });
    }

    render() {
      const { t } = this.props;
      return (
        <AddWrapper>
          <TextInput
            id="device-tag-adder-name"
            label={t('device_tags.label_name')}
            placeholder={t('device_tags.field_hint')}
            onChange={({ target: { value } }) => this.inputTagNameValue(value)}
            input={{ value: this.state.name }}
            meta={{}}
          />
          <TextInput
            id="device-tag-adder-value"
            label={t('device_tags.label_value')}
            placeholder={t('device_tags.field_hint')}
            onChange={({ target: { value } }) => this.setState({ value })}
            input={{ value: this.state.value }}
            meta={{}}
          />
          <Button
            id="device-tag-adder-add"
            type="button"
            onClick={this.add}
            style={{ marginTop: '40px' }}
          >
            {t('device_tags.add')}
          </Button>
        </AddWrapper>
      );
    }
  }
);

const ShelfSlotAdder = translate()(
  class extends Component {
    state = {
      shelfId: '',
      slotId: '',
    };

    add = () => {
      if (this.state.shelfId === '' || this.state.slotId === '') {
        return;
      }

      this.props.onAdd(this.state);
      this.setState({ shelfId: '' });
    };

    render() {
      const { t } = this.props;
      return (
        <AddWrapper>
          <SliderWrapper>
            <Field
              name="ShelfIds"
              component={FormSelect}
              options={shelfIds}
              onChange={selectedShelf => {
                this.setState({ shelfId: selectedShelf.value });
              }}
            />
          </SliderWrapper>

          <SliderWrapper>
            <Field
              name="SlotIds"
              component={FormSelect}
              options={slotIds}
              onChange={selectedSlot => {
                this.setState({ slotId: selectedSlot.value });
              }}
            />
          </SliderWrapper>

          <Button
            id="device-shelf-slot-adder-add"
            type="button"
            onClick={this.add}
            style={{ marginTop: '40px' }}
          >
            {t('device_shelf_config.add')}
          </Button>
        </AddWrapper>
      );
    }
  }
);

const RenderPreview = ({ input: { value } }) => <span>{value}</span>;

const DefinitionList = styled.dl`
  color: ${props => props.theme.secondary};
  fill: ${props => props.theme.secondary};
  display: grid;
  grid-template-columns: 180px fit-content(150px) 30px;
  width: 360px;
  margin: 30px auto 20px;
  line-height: 30px;
  font-size: 14px;
`;

const Term = styled.dt`
  text-align: right;
`;

const Definition = styled.dd`
  text-align: left;
  &:before {
    content: ':';
    margin: 0px 5px;
`;

const DeleteButton = styled.button`
  fill: ${props => props.theme.secondary};
  height: 30px;
  padding: 1px;
  border: none;
  background: none;
  margin-left: 10px;
`;

const PreviewTag = ({ tag, remove }) => (
  <React.Fragment>
    <Term>
      <Field type="text" name={`${tag}.name`} component={RenderPreview} />
    </Term>
    <Definition>
      <Field type="text" name={`${tag}.value`} component={RenderPreview} />
    </Definition>
    <DeleteButton type="button" onClick={remove}>
      <Cross />
    </DeleteButton>
  </React.Fragment>
);

const PreviewShelfSlots = ({ shelfSlot, remove }) => (
  <React.Fragment>
    <Term>
      <Field
        type="text"
        name={`${shelfSlot}.shelfId`}
        component={RenderPreview}
      />
    </Term>
    <Definition>
      <Field
        type="text"
        name={`${shelfSlot}.slotId`}
        component={RenderPreview}
      />
    </Definition>
    <DeleteButton type="button" onClick={remove}>
      <Cross />
    </DeleteButton>
  </React.Fragment>
);

const Wrapper = styled.div``;

const renderShelfSlotSelection = ({ fields }) => (
  <Wrapper>
    <ShelfSlotAdder
      onAdd={shelfSlot => {
        fields.push(shelfSlot);
      }}
    />
    <DefinitionList>
      {fields.map((shelfSlot, index) => (
        <PreviewShelfSlots
          key={index}
          shelfSlot={shelfSlot}
          remove={() => fields.remove(index)}
        />
      ))}
    </DefinitionList>
  </Wrapper>
);

const renderTags = ({ fields }) => (
  <Wrapper>
    <TagAdder
      onAdd={tag => {
        fields.push(tag);
      }}
    />
    <DefinitionList>
      {fields.map((tag, index) => (
        <PreviewTag key={index} tag={tag} remove={() => fields.remove(index)} />
      ))}
    </DefinitionList>
  </Wrapper>
);

const cardPaymentOption = [
  'mypos',
  'nayax',
  'nayax-mini',
  'Axis-ingenico',
  'Axis-ingenico-Mews',
  'wipay',
  'Logos-Valina',
  'nets',
  'NoPayN',
  '',
];

const DeviceAdditionalPage = ({ subType, CardTerminal, handleSubmit, t }) => (
  <React.Fragment>
    <Heading text={t('device_additional.title')} />
    <form onSubmit={handleSubmit}>
      {hasOptionalSetting(subType, 'SHELF_CONFIG') && (
        <>
          <SubHeading text={t('device_shelf_config.title')} />
          <FieldArray name="Shelves" component={renderShelfSlotSelection} />
        </>
      )}

      {hasOptionalSetting(subType, 'SHELF_COUNT') && (
        <>
          <SubHeading text={t('device_additional.shelf_count')} />
          <SliderWrapper>
            <Field
              id="shelf-count-slider"
              component={Slider}
              name="ShelfCount"
              min={5}
              max={6}
              defaultValue={5}
            />
          </SliderWrapper>
        </>
      )}

      {hasOptionalSetting(subType, 'CARD_PAYMENT') && (
        <>
          <SubHeading text={t('device_additional.card_payment')} />
          <CardPaymentWrapper>
            {cardPaymentOption.map((option, i) => {
              if (option === '') {
                return (
                  <RadioField
                    key={i}
                    name="CardTerminal"
                    type="radio"
                    component="input"
                    value="-"
                    label="No card payment"
                    textCapitalize
                  />
                );
              }
              return (
                <RadioField
                  key={i}
                  name="CardTerminal"
                  type="radio"
                  component="input"
                  value={option}
                  label={option}
                  textCapitalize
                />
              );
            })}
          </CardPaymentWrapper>
        </>
      )}

      {CardTerminal === 'Axis-ingenico' && (
        <div>
          <SubHeading text={t('device_antenor_tpv.label')} />
          <Field name="antenorTPVnr" type="text" component={TextInput} />
        </div>
      )}
      {CardTerminal === 'wipay' && (
        <div>
          <SubHeading text={t('wipay_terminal_id.label')} />
          <Field name="wipayTerminalId" type="text" component={TextInput} />
        </div>
      )}
      <SubHeading text={t('device_tags.title')} />
      <FieldArray name="Tags" component={renderTags} />

      <CardPaymentErrorWrapper>
        <Field name="CardTerminal" component={renderError} />
      </CardPaymentErrorWrapper>
    </form>
  </React.Fragment>
);

const selector = formValueSelector(WIZARD_FORM_NAME);
const mapStateToProps = state => ({
  type: selector(state, 'Type'),
  subType: selector(state, 'SubType'),
  CardTerminal: selector(state, 'CardTerminal'),
});

export default translate()(connect(mapStateToProps)(DeviceAdditionalPage));
